/**
 * Barcodes with the camera. Android Chrome has BarcodeDetector; Safari on the iPhone does not, so
 * there zxing-wasm reads the frames. Its WebAssembly file is delivered by our own server (and kept
 * by the service worker for the cellar without signal), never fetched from a CDN.
 */
import wasmUrl from "zxing-wasm/reader/zxing_reader.wasm?url";

type Detect = (frame: ImageData | HTMLVideoElement) => Promise<string | null>;

interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<{ rawValue: string }[]>;
}
declare const BarcodeDetector:
  | { new (opts: { formats: string[] }): BarcodeDetectorLike; getSupportedFormats(): Promise<string[]> }
  | undefined;

export type ScanEngine = "BarcodeDetector" | "zxing-wasm";

export async function createDetector(): Promise<{ engine: ScanEngine; detect: Detect }> {
  if (typeof BarcodeDetector !== "undefined") {
    try {
      const formats = await BarcodeDetector.getSupportedFormats();
      if (formats.includes("ean_13")) {
        const detector = new BarcodeDetector({ formats: ["ean_13", "ean_8", "code_128", "qr_code"] });
        return {
          engine: "BarcodeDetector",
          detect: async (source) => {
            if (!(source instanceof HTMLVideoElement)) return null;
            const found = await detector.detect(source);
            return found[0]?.rawValue ?? null;
          },
        };
      }
    } catch {
      // fall through to zxing
    }
  }
  return zxingDetector();
}

/** The reader for Safari (and the fallback): zxing-wasm with the WebAssembly file from our server. */
export async function zxingDetector(): Promise<{ engine: ScanEngine; detect: Detect }> {
  const zxing = await import("zxing-wasm/reader");
  zxing.prepareZXingModule({ overrides: { locateFile: (path: string, prefix: string) => (path.endsWith(".wasm") ? wasmUrl : prefix + path) }, fireImmediately: true });
  return {
    engine: "zxing-wasm",
    detect: async (source) => {
      if (!(source instanceof ImageData)) return null;
      const results = await zxing.readBarcodes(source, { formats: ["EAN13", "EAN8", "Code128", "QRCode"], tryHarder: true, maxNumberOfSymbols: 1 });
      return results.find((r) => r.isValid)?.text ?? null;
    },
  };
}

/** Reads a frame of the video into ImageData (for zxing), cropped to the middle band. */
export function grab(video: HTMLVideoElement, canvas: HTMLCanvasElement): ImageData | null {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) return null;
  const bandH = Math.round(h * 0.5);
  canvas.width = w;
  canvas.height = bandH;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(video, 0, (h - bandH) / 2, w, bandH, 0, 0, w, bandH);
  return ctx.getImageData(0, 0, w, bandH);
}
